import { CardColor } from './cards.js';
import { RoomRules } from './rules.js';
import { GameEvent, RoomSummary, RoomView } from './types.js';

/* ------------------------------------------------------------------ */
/* Game actions                                                        */
/* ------------------------------------------------------------------ */

/**
 * Every mutation a player can request. All of them are validated server-side;
 * the client never mutates authoritative state on its own.
 */
export type GameAction =
  /** Play a card from hand. `chosenColor` is required for wilds. */
  | { type: 'play'; cardId: string; chosenColor?: CardColor; declareUno?: boolean }
  /** Draw from the pile (or eat the pending +2/+4 stack). */
  | { type: 'draw' }
  /** End the turn after drawing without playing the drawn card. */
  | { type: 'pass' }
  /** Press UNO for yourself. */
  | { type: 'call_uno' }
  /** Catch someone who is on one card and never pressed UNO. */
  | { type: 'judge'; targetId: string }
  /** Answer the Wild Draw Four challenge prompt. */
  | { type: 'challenge_wild_four'; challenge: boolean };

/* ------------------------------------------------------------------ */
/* Client -> Server                                                    */
/* ------------------------------------------------------------------ */

export interface CreateRoomInput {
  name: string;
  password?: string;
  isPublic: boolean;
  maxPlayers: number;
  rules: Partial<RoomRules>;
}

export type ClientMessage =
  /** First frame on every socket. Restores an existing session when `sessionId` is known. */
  | { t: 'hello'; sessionId?: string; nickname?: string }
  /** Nickname and avatar emoji travel together — both are "who you are". */
  | { t: 'set_profile'; nickname: string; emoji: string }
  | { t: 'list_rooms' }
  | { t: 'create_room'; input: CreateRoomInput }
  | { t: 'join_room'; roomId: string; password?: string }
  | { t: 'leave_room' }
  | { t: 'set_ready'; ready: boolean }
  | { t: 'update_rules'; rules: Partial<RoomRules> }
  /** Host-only. How many AI players to seat when the game starts. */
  | { t: 'set_bots'; count: number }
  | { t: 'kick'; playerId: string }
  /** Table talk. Broadcast to the room as a `chat` event. */
  | { t: 'chat'; text: string }
  /**
   * The only message that mutates game state.
   * `seq` is the room version the client believed it was acting on — the server
   * rejects anything stale, which is what makes concurrent clicks safe.
   */
  | { t: 'action'; seq: number; action: GameAction }
  | { t: 'ping' };

/* ------------------------------------------------------------------ */
/* Server -> Client                                                    */
/* ------------------------------------------------------------------ */

export type ErrorCode =
  | 'bad_request'
  | 'no_session'
  | 'room_not_found'
  | 'room_full'
  | 'wrong_password'
  | 'already_in_room'
  | 'not_in_room'
  | 'not_host'
  | 'game_in_progress'
  | 'not_enough_players'
  | 'stale_action'
  | 'illegal_action'
  | 'rate_limited'
  | 'nickname_invalid';

export type ServerMessage =
  | {
      t: 'session';
      sessionId: string;
      nickname: string;
      emoji: string;
      /** set when the socket resumed an in-progress room */
      roomId: string | null;
    }
  | { t: 'rooms'; rooms: RoomSummary[] }
  /** Full authoritative snapshot. `events` narrate what changed since the last snapshot. */
  | { t: 'room'; room: RoomView; events: GameEvent[] }
  | { t: 'left_room' }
  | { t: 'error'; code: ErrorCode; message: string }
  /**
   * The client's action lost a race or was illegal. `room` is the true state to
   * roll back to — the client discards its optimistic prediction and re-renders.
   */
  | { t: 'action_rejected'; code: ErrorCode; message: string; room: RoomView }
  | { t: 'pong' };

/* ------------------------------------------------------------------ */
/* Shared constants                                                    */
/* ------------------------------------------------------------------ */

export const PROTOCOL = {
  /** Grace period before a disconnected player forfeits the match. */
  RECONNECT_GRACE_MS: 3 * 60 * 1000,
  /** Seconds the next player gets to answer a Wild Draw Four. */
  CHALLENGE_SECONDS: 12,
  /** Total participants needed to start — humans and bots count alike. */
  MIN_PLAYERS: 2,
  MAX_PLAYERS: 10,
  /** How long a bot "thinks" before acting, so the table stays readable. */
  BOT_THINK_MIN_MS: 700,
  BOT_THINK_MAX_MS: 1600,
  NICKNAME_MAX: 16,
  /** Longest chat message accepted; longer ones are trimmed, not rejected. */
  CHAT_MAX: 160,
  ROOM_NAME_MAX: 24,
  /** Heartbeat interval; sockets silent for 2x this are dropped. */
  HEARTBEAT_MS: 15_000,
} as const;

export function isValidNickname(name: string): boolean {
  const trimmed = name.trim();
  return trimmed.length >= 1 && trimmed.length <= PROTOCOL.NICKNAME_MAX;
}

/** Reserved for AI players, so a human cannot pose as one. */
export const BOT_EMOJI = '\u{1F916}';

/**
 * Avatars a player may choose. Deliberately excludes the bot's robot face.
 * Kept server-side as well as client-side so the restriction is enforced, not
 * merely presented.
 */
export const AVATAR_EMOJI = [
  '\u{1F600}', '\u{1F60E}', '\u{1F913}', '\u{1F92A}', '\u{1F973}', '\u{1F9D0}', '\u{1F60D}', '\u{1F634}',
  '\u{1F431}', '\u{1F436}', '\u{1F98A}', '\u{1F43C}', '\u{1F428}', '\u{1F42F}', '\u{1F435}', '\u{1F438}',
  '\u{1F984}', '\u{1F427}', '\u{1F989}', '\u{1F41D}', '\u{1F419}', '\u{1F980}', '\u{1F996}', '\u{1F995}',
  '\u{1F345}', '\u{1F355}', '\u{1F369}', '\u{1F36A}', '\u{1F353}', '\u{1F34B}', '\u{1F951}', '\u{1F9C1}',
  '\u{26BD}', '\u{1F3C0}', '\u{1F3B8}', '\u{1F3B2}', '\u{1F680}', '\u{1F31F}', '\u{1F525}', '\u{1F308}',
] as const;

export function isValidEmoji(emoji: string): boolean {
  return emoji === '' || (AVATAR_EMOJI as readonly string[]).includes(emoji);
}
